import { motion } from 'framer-motion';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Link } from 'react-router-dom';
import useSEO from '@/hooks/useSEO';

const FairUse = () => {
  useSEO({
    title: 'Fair Use Policy — EpisodeKit',
    description: 'EpisodeKit fair use policy. Acceptable use, content ownership, and the limits that keep the platform fair for everyone.',
    path: '/fair-use',
    image: '/og/episodekit-og-fair-use.jpg',
    index: true,
  });
  return (
    <div className="min-h-screen bg-[#050505]">
      <div className="noise-overlay" />
      <Navbar />

      <section className="pt-32 pb-24 px-6 md:px-12">
        <div className="max-w-4xl mx-auto">
          <motion.h1 
            className="text-4xl md:text-5xl font-unbounded font-bold mb-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            Fair Use Policy
          </motion.h1>
          
          <motion.div
            className="prose prose-invert max-w-none"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <p className="text-neutral-400 mb-8">
              Last Updated: December 19, 2025
            </p>

            <div className="space-y-6 text-neutral-300">
              <section>
                <h2 className="text-2xl font-unbounded font-bold text-white mb-4">What is Fair Use?</h2>
                <p>
                  Our Fair Use Policy ensures that EpisodeKit remains stable, fast, and accessible to all users. We designed our service for podcasters creating genuine content for normal human-driven podcast workflows, not for automated systems, bulk processing operations, or excessive usage patterns that degrade platform performance.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-unbounded font-bold text-white mb-4">Normal Usage Expectations</h2>
                <p>
                  We expect users to:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Upload legitimate podcast audio files for their own podcasting activities</li>
                  <li>Process episodes at a reasonable frequency aligned with typical podcast production schedules</li>
                  <li>Use the service for personal or business podcasting purposes within the scope of normal human-driven workflows</li>
                  <li>Stay within their plan's monthly minute allowance</li>
                  <li>Refrain from automated, continuous, or bulk processing operations</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-unbounded font-bold text-white mb-4">What We Consider Excessive or Abusive</h2>
                <p>
                  Examples of usage that violates this policy include, but are not limited to:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Automated or bot-driven uploads or API abuse</li>
                  <li>Uploading content you don't own or lack proper rights to process</li>
                  <li>Repeatedly uploading identical or near-identical files for testing or other purposes</li>
                  <li>Using the service to circumvent usage limits through multiple accounts or deceptive practices</li>
                  <li>Attempting to resell, redistribute, or white-label our AI-generated content as your own service</li>
                  <li>Any activity that degrades platform performance, availability, or reliability for other users</li>
                  <li>Continuous or bulk processing inconsistent with normal podcast production workflows</li>
                </ul>
                <p className="mt-4">
                  <strong>EpisodeKit has sole discretion</strong> in determining what constitutes excessive, abusive, or otherwise prohibited usage under this policy. We are not required to disclose internal monitoring thresholds, detection methods, performance metrics, or the specific criteria used to identify violations.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-unbounded font-bold text-white mb-4">Hard Limits for Platform Stability</h2>
                <p>
                  To protect our infrastructure and ensure fair access for all users, we enforce the following monthly minute caps:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong>Free Plan:</strong> 60 minutes per month</li>
                  <li><strong>Basic Plan:</strong> 300 minutes per month</li>
                  <li><strong>Pro Plan:</strong> 900 minutes per month</li>
                  <li><strong>Unlimited Plan:</strong> 3,000 minutes per month</li>
                </ul>
                <p className="mt-4">
                  These limits reset at the start of each calendar month. Usage is counted based on the full duration of each uploaded episode. Longer episodes consume more of your monthly minutes allowance.
                </p>
                <p className="mt-4">
                  <strong>Important Note on the Unlimited Plan:</strong> The Unlimited plan is subject to this Fair Use Policy and is intended for normal human-driven podcast workflows. It is <strong>NOT</strong> intended for automated processing, continuous bulk operations, or usage patterns inconsistent with typical podcast production. Despite the name "Unlimited," all usage on this plan must comply with the Fair Use Policy, and the 3,000 minute per month cap exists to prevent abuse and ensure platform stability for all users.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-unbounded font-bold text-white mb-4">Enforcement and Consequences</h2>
                <p>
                  If we detect patterns or activities that violate this Fair Use Policy, we reserve the right to take any of the following actions, at our sole discretion:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Temporarily throttle or limit your processing speed or capacity</li>
                  <li>Contact you to discuss your usage patterns and request changes</li>
                  <li>Require you to upgrade to a higher plan or adjust your usage to comply with this policy</li>
                  <li>Immediately suspend or terminate your account and access to the service</li>
                  <li>Take legal action to recover damages or enforce our rights</li>
                </ul>
                <p className="mt-4">
                  <strong>We may attempt to contact you before taking action</strong> in some cases, particularly for inadvertent or borderline violations. However, <strong>we reserve the right to take immediate action without prior notice</strong> in cases involving automated abuse, malicious activity, severe platform impact, or patterns clearly inconsistent with legitimate podcast production.
                </p>
                <p className="mt-4">
                  Our failure to enforce this policy in any particular instance does not constitute a waiver of our right to enforce it in the future.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-unbounded font-bold text-white mb-4">Contact Us</h2>
                <p>
                  If you believe you've been flagged in error, if you need higher limits for legitimate use cases, or if you have questions about this Fair Use Policy, please contact us at:
                </p>
                <p className="mt-2">
                  <a href="mailto:support@episodekit.net" className="text-primary hover:underline">
                    support@episodekit.net
                  </a>
                </p>
                <p className="mt-4">
                  We are committed to working with creators and teams with legitimate high-volume needs, and may consider reasonable limit adjustments on a case-by-case basis.
                </p>
              </section>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default FairUse;